import React, { useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { rawApiClient } from '../../services/api';
import RecoveryCodesPanel from './RecoveryCodesPanel';

/**
 * For someone with two-step sign-in on (1 Oct 2026): make a new set of recovery codes
 * (confirmed with a current app code; the old set stops working), and forget trusted
 * devices so every device asks for the code again.
 */
export default function TwoStepManage({ status, onChanged }) {
  const [mode, setMode] = useState('idle'); // idle | confirm | codes
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const left = status.recoveryCodesLeft;
  const trusted = status.trustedDevices || 0;

  const makeCodes = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await rawApiClient.post('/auth/mfa/recovery-codes', { code: code.replace(/\s+/g, '') });
      setCodes(res.data.recoveryCodes || []);
      setSaved(false);
      setMode('codes');
    } catch (err) {
      setError(err?.response?.data?.errors?.[0]?.msg || 'That code is not correct.');
    } finally {
      setBusy(false);
    }
  };

  const forgetDevices = async () => {
    setBusy(true);
    setError('');
    try {
      await rawApiClient.post('/auth/devices/forget');
      setNotice('Done. Every device will ask for a code at its next sign-in.');
      if (onChanged) onChanged();
    } catch (_) {
      setError('Could not forget your devices. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'codes') {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="success" sx={{ mb: 2 }}>New recovery codes made. Your old codes no longer work.</Alert>
        <RecoveryCodesPanel codes={codes} saved={saved} onSavedChange={setSaved} />
        <Button variant="contained" size="small" disabled={!saved} onClick={() => { setMode('idle'); setCode(''); if (onChanged) onChanged(); }}>
          Done
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color={left <= 3 ? 'warning.main' : 'text.secondary'}>
        On. You have {left} recovery code{left === 1 ? '' : 's'} left.
        {trusted > 0 && ` ${trusted} device${trusted === 1 ? ' is' : 's are'} trusted to skip the code.`}
      </Typography>
      {notice && <Alert severity="success" sx={{ mt: 1 }}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {mode === 'confirm' ? (
        <Box component="form" onSubmit={makeCodes} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          <TextField
            id="mfa-regenerate-code" size="small" label="Code from your app" inputMode="numeric" autoComplete="one-time-code"
            value={code} onChange={e => setCode(e.target.value)} inputProps={{ maxLength: 7 }} autoFocus
          />
          <Button type="submit" size="small" variant="contained" disabled={busy || code.replace(/\s+/g, '').length !== 6}>Make new codes</Button>
          <Button size="small" onClick={() => { setMode('idle'); setCode(''); setError(''); }}>Cancel</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => { setMode('confirm'); setNotice(''); }} disabled={busy}>
            Make new recovery codes
          </Button>
          {trusted > 0 && (
            <Button size="small" variant="outlined" onClick={forgetDevices} disabled={busy}>
              Forget my trusted devices
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
